import { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Calculator, BookOpen, Gauge } from 'lucide-react'

/**
 * Compact 3-up score tiles for the dashboard: Math · Reading & Writing · Total.
 * Each tile shows the current score big, the predicted score small, a trend
 * chip, and a thin bar of where the score sits on its scale (200–800 for a
 * section, 400–1600 for the total).
 */

const SECTION_MIN = 200
const SECTION_MAX = 800
const TOTAL_MIN = 400
const TOTAL_MAX = 1600

// Returns null for a missing/unknown direction so the caller can omit the
// chip entirely — a brand-new user with one challenge shouldn't see a
// misleading "Stable" badge.
const trendLabel = (trend) => {
  switch (trend?.direction) {
    case 'improving':
    case 'mixed_improving':
      return 'Improving'
    case 'declining':
    case 'mixed_declining':
      return 'Declining'
    case 'mixed':
      return 'Mixed'
    case 'stable':
      return 'Stable'
    default:
      return null
  }
}

const clampPct = (n) => Math.max(0, Math.min(100, Math.round(n)))

// A composite total is only meaningful when both sections have the value;
// summing with a 0 fallback would show a wrong total when only one subject
// has data.
const sumIfBoth = (a, b) => (a != null && b != null ? a + b : null)

// Rough numeric rank for a section trend direction.
const DIRECTION_RANK = {
  improving: 1,
  mixed_improving: 1,
  stable: 0,
  mixed: 0,
  declining: -1,
  mixed_declining: -1
}

/**
 * Derive a sensible composite direction from the two section trends so the
 * synthesized Total trend chip isn't permanently "Stable".
 */
const deriveCombinedDirection = (mathTrend, rwTrend) => {
  const m = DIRECTION_RANK[mathTrend?.direction] ?? 0
  const r = DIRECTION_RANK[rwTrend?.direction] ?? 0
  if ((m > 0 && r < 0) || (m < 0 && r > 0)) return 'mixed'
  if (m > 0 || r > 0) return m >= 0 && r >= 0 ? 'improving' : 'mixed_improving'
  if (m < 0 || r < 0) return m <= 0 && r <= 0 ? 'declining' : 'mixed_declining'
  return 'stable'
}

/**
 * Bar position is always "where this score sits on the score scale" — one
 * consistent meaning whether or not a prediction exists. Uses the current
 * score when available, otherwise the predicted score.
 */
const scalePct = (current, predicted, min, max) => {
  const score = current ?? predicted
  if (score == null) return 0
  return clampPct(((score - min) / (max - min)) * 100)
}

const SkeletonTile = ({ isTotal }) => (
  <div
    className={`rounded-lg border p-4 ${
      isTotal
        ? 'bg-primary-50/60 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800'
        : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
    }`}
    aria-hidden="true"
  >
    <div className="animate-pulse">
      <div className="h-3 w-24 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-3 h-8 w-20 rounded bg-gray-200 dark:bg-gray-700" />
      <div className="mt-3 h-1.5 w-full rounded-full bg-gray-200 dark:bg-gray-700" />
    </div>
  </div>
)

const Tile = ({ icon: Icon, label, data, min, max, isTotal = false }) => {
  const current = data?.currentScore ?? null
  const predicted = data?.predictedScore ?? null
  const hasData = current !== null || predicted !== null
  const pct = scalePct(current, predicted, min, max)
  const barTitle = hasData ? `${current ?? predicted} on the ${min}–${max} scale` : undefined
  const trend = trendLabel(data?.trend)

  return (
    <div
      className={`rounded-lg border p-4 ${
        isTotal
          ? 'bg-primary-50/60 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800'
          : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
      }`}
    >
      <div className="flex items-center justify-between gap-2">
        <div
          className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide ${
            isTotal ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </div>
        {hasData && trend && (
          <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 whitespace-nowrap">
            {trend}
          </span>
        )}
      </div>

      <div className="mt-2 flex items-end gap-3">
        {hasData ? (
          <>
            <span
              className={`text-3xl font-bold leading-none ${
                isTotal
                  ? 'text-primary-700 dark:text-primary-300'
                  : 'text-gray-900 dark:text-gray-100'
              }`}
            >
              {current ?? '—'}
            </span>
            {predicted !== null && (
              <div className="text-xs leading-tight">
                <div className="text-warning-600 dark:text-warning-400 font-semibold">
                  {predicted}
                </div>
                <div
                  className={isTotal ? 'text-primary-400 dark:text-primary-500' : 'text-gray-400'}
                >
                  predicted
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <span className="text-3xl font-bold leading-none text-gray-300 dark:text-gray-600">
              —
            </span>
            <div className="text-xs text-gray-400 leading-tight">
              No data yet
              <br />
              Answer 10+ questions
            </div>
          </>
        )}
      </div>

      <div
        className={`mt-3 h-1.5 rounded-full overflow-hidden ${
          isTotal ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-gray-100 dark:bg-gray-700'
        }`}
        role="progressbar"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={hasData ? (current ?? predicted) : undefined}
        aria-label={`${label} score on the ${min}–${max} scale`}
        title={barTitle}
      >
        {hasData && (
          <div
            className={`h-full rounded-full ${isTotal ? 'bg-primary-600' : 'bg-primary-500'}`}
            style={{ width: `${pct}%` }}
          />
        )}
      </div>
    </div>
  )
}

const ScoreSummaryTiles = ({ math, readingWriting, combined, loading = false }) => {
  const combinedData = useMemo(
    () =>
      combined ?? {
        currentScore: sumIfBoth(math?.currentScore, readingWriting?.currentScore),
        predictedScore: sumIfBoth(math?.predictedScore, readingWriting?.predictedScore),
        trend: {
          direction: deriveCombinedDirection(math?.trend, readingWriting?.trend),
          change: (math?.trend?.change || 0) + (readingWriting?.trend?.change || 0)
        }
      },
    [math, readingWriting, combined]
  )

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SkeletonTile />
        <SkeletonTile />
        <SkeletonTile isTotal />
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <Tile icon={Calculator} label="Math" data={math} min={SECTION_MIN} max={SECTION_MAX} />
      <Tile
        icon={BookOpen}
        label="Reading & Writing"
        data={readingWriting}
        min={SECTION_MIN}
        max={SECTION_MAX}
      />
      <Tile
        icon={Gauge}
        label="Total"
        data={combinedData}
        min={TOTAL_MIN}
        max={TOTAL_MAX}
        isTotal
      />
    </div>
  )
}

const scoreSectionShape = PropTypes.shape({
  currentScore: PropTypes.number,
  predictedScore: PropTypes.number,
  trend: PropTypes.shape({
    direction: PropTypes.string,
    change: PropTypes.number
  })
})

SkeletonTile.propTypes = {
  isTotal: PropTypes.bool
}

Tile.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  data: scoreSectionShape,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  isTotal: PropTypes.bool
}

ScoreSummaryTiles.propTypes = {
  math: scoreSectionShape,
  readingWriting: scoreSectionShape,
  combined: scoreSectionShape,
  loading: PropTypes.bool
}

export default ScoreSummaryTiles
